"use client";

import type React from "react";
import styles from "./dotted-background.module.css";
export type ColorVariant = "default" | "fire" | "ocean" | "forest" | "neon" | "sunset" | "cyber" | "galaxy";
export type AnimationSpeed = "slow" | "normal" | "fast" | "ultra";
export type Intensity = "low" | "medium" | "high" | "ultra";
export type Rotation = 0 | 45 | 90 | 135 | 180 | 225 | 270 | 315;
interface DottedBackgroundProps {
  /**
   * Additional CSS classes to apply to the container
   * @example "z-10 opacity-50"
   */
  className?: string;

  /**
   * Background color of the container
   * @default "#000"
   * @example "#1a1a1a" for dark gray background
   * Accepts any valid CSS color value (hex, rgb, hsl, etc.)
   */
  backgroundColor?: string;

  /**
   * Color scheme for the animated gradients
   * @default "default"
   * Available variants: "default", "fire", "ocean", "forest", "neon", "sunset", "cyber", "galaxy"
   */
  variant?: ColorVariant;

  /**
   * Custom primary gradient color (overrides variant)
   * @default undefined (uses variant color)
   * @example "#ff4500" for orange-red
   * Accepts any valid CSS color value
   */
  primaryColor?: string;

  /**
   * Custom secondary gradient color (overrides variant)
   * @default undefined (uses variant color)
   * @example "#ff0000" for red
   * Accepts any valid CSS color value
   */
  secondaryColor?: string;

  /**
   * Color of the background dots
   * @default undefined (uses variant-based color)
   * @example "hsl(200 50% 8%)" for blue-tinted dark dots
   * Accepts any valid CSS color value
   */
  dotColor?: string;

  /**
   * Opacity of the background dots
   * @default 1
   * @min 0 (transparent dots)
   * @max 1 (fully opaque dots)
   * Controls the visibility of the dot pattern
   */
  dotOpacity?: number;

  /**
   * Opacity of the dots on hover (for group-hover interactions)
   * @default undefined (uses dotOpacity value)
   * @min 0 (transparent dots on hover)
   * @max 1 (fully opaque dots on hover)
   * Controls dot visibility during hover states
   */
  dotHoverOpacity?: number;

  /**
   * Additional CSS classes that affect the dots specifically
   * @default ""
   * @example "blur-sm" to blur the dots
   * @example "contrast-150" to increase dot contrast
   * These classes are applied to the container and affect the dot pseudo-element
   */
  dotClassName?: string;

  /**
   * Speed of the animations
   * @default "normal"
   * - "slow": 200s gradient movement, 8s hue rotation
   * - "normal": 150s gradient movement, 5s hue rotation
   * - "fast": 100s gradient movement, 3s hue rotation
   * - "ultra": 50s gradient movement, 2s hue rotation
   */
  animationSpeed?: AnimationSpeed;

  /**
   * Intensity of the blur and brightness effects
   * @default "high"
   * - "low": 1.5em blur, 5x brightness
   * - "medium": 2.5em blur, 7x brightness
   * - "high": 3em blur, 9x brightness
   * - "ultra": 4em blur, 12x brightness
   */
  intensity?: Intensity;

  /**
   * Size of the background dots in pixels
   * @default 2
   * @example 1 for very small dots
   * @example 4 for larger dots
   * Controls the size of individual dots in the pattern
   */
  dotSize?: number;

  /**
   * Spacing between dots in pixels
   * @default undefined (uses CSS module default)
   * @example 20 for 20px spacing between dots
   * @example 10 for tighter spacing
   * @example 30 for wider spacing
   * Controls the background-size property for dot pattern
   */
  dotSpacing?: number;

  /**
   * Rotation angle of the gradient pattern
   * @default -45 (equivalent to 315)
   * @example 90 for vertical gradient movement
   * Accepts: 0, 45, 90, 135, 180, 225, 270, 315 (degrees)
   */
  rotation?: Rotation;

  /**
   * Custom blur intensity in CSS units
   * @default undefined (uses intensity preset)
   * @example "2em" for 2em blur
   * Overrides the intensity blur setting when provided
   */
  customBlur?: string;

  /**
   * Custom brightness multiplier
   * @default undefined (uses intensity preset)
   * @example 10 for 10x brightness
   * @min 1 (normal brightness)
   * @max 20 (very bright, may cause performance issues)
   * Overrides the intensity brightness setting when provided
   */
  customBrightness?: number;

  /**
   * Custom animation duration for gradient movement
   * @default undefined (uses animationSpeed preset)
   * @example "120s" for 120 second duration
   * Accepts any valid CSS time value (s, ms)
   */
  customAnimationDuration?: string;

  /**
   * Custom animation duration for hue rotation
   * @default undefined (uses animationSpeed preset)
   * @example "4s" for 4 second hue rotation
   * Accepts any valid CSS time value (s, ms)
   */
  customHueDuration?: string;

  /**
   * Disable the hue rotation animation
   * @default false
   * When true, only the gradient movement animation will play
   */
  disableHueRotation?: boolean;

  /**
   * Disable the gradient movement animation
   * @default false
   * When true, only the hue rotation animation will play
   */
  disableGradientMovement?: boolean;

  /**
   * Opacity of the gradient layer (animated background gradients)
   * @default 1
   * @min 0 (transparent gradients)
   * @max 1 (fully opaque gradients)
   * Controls the visibility of the animated gradient background
   */
  gradientOpacity?: number;

  /**
   * Opacity of the gradient layer on hover (for group-hover interactions)
   * @default undefined (uses gradientOpacity value)
   * @min 0 (transparent gradients on hover)
   * @max 1 (fully opaque gradients on hover)
   * Controls gradient visibility during hover states
   */
  gradientHoverOpacity?: number;

  /**
   * Z-index of the gradient layer (animated background gradients)
   * @default 0
   * @example 10 to place above other content
   * @example -1 to place behind other content
   * Controls stacking order of the gradient layer (::before pseudo-element)
   */
  gradientZIndex?: number;

  /**
   * Z-index of the dot layer
   * @default 1
   * @example 5 to place above gradients
   * @example 15 to place above overlays
   * Controls stacking order of the dot layer (::after pseudo-element)
   */
  dotZIndex?: number;

  /**
   * Z-index value for the container
   * @default undefined
   * @example -1 to place behind other content
   * @example 10 to place above other content
   */
  zIndex?: number;

  /**
   * Z-index of the circle overlay
   * @default 2
   * @example 10 to place above dots
   * @example 20 to place above all other layers
   */
  circleOverlayZIndex?: number;

  /**
   * Z-index of the vignette overlay
   * @default 2
   * @example 10 to place above dots
   * @example 20 to place above all other layers
   */
  vignetteZIndex?: number;

  /**
   * Z-index of the inner glow overlay
   * @default 2
   * @example 10 to place above dots
   * @example 20 to place above all other layers
   */
  innerGlowZIndex?: number;

  /**
   * Enable circular overlay in the center
   * @default false
   * When true, adds a circular overlay that starts from the center and fades to transparent
   */
  enableCircleOverlay?: boolean;

  /**
   * Color of the circular overlay
   * @default "#000000" (black)
   * @example "#ffffff" for white overlay
   * @example "rgba(0, 0, 0, 0.8)" for semi-transparent black
   * Accepts any valid CSS color value
   */
  circleOverlayColor?: string;

  /**
   * Opacity of the circular overlay
   * @default 0.8
   * @min 0 (transparent)
   * @max 1 (fully opaque)
   */
  circleOverlayOpacity?: number;

  /**
   * Size of the circular overlay (how far it extends before fading)
   * @default 30
   * @min 0 (very small circle)
   * @max 80 (large circle covering most of the area)
   * Represents the percentage of the radius where the color starts fading
   */
  circleOverlaySize?: number;

  /**
   * Additional CSS classes to apply to the overlay
   * @default ""
   * @example "z-50 opacity-50"
   */
  overlayClassName?: string;

  /**
   * Enable vignette overlay (darkening from edges to center)
   * @default false
   * When true, adds a vignette overlay that darkens the edges and fades to transparent in the center
   */
  enableVignette?: boolean;

  /**
   * Color of the vignette overlay
   * @default "rgb(0,0,0)" (black)
   * @example "rgb(25,25,25)" for dark gray vignette
   * @example "rgba(0,0,0,0.8)" for semi-transparent black
   * Accepts any valid CSS color value
   */
  vignetteColor?: string;

  /**
   * Opacity of the vignette overlay
   * @default 0.9
   * @min 0 (transparent)
   * @max 1 (fully opaque)
   */
  vignetteOpacity?: number;

  /**
   * Size of the vignette (how far it extends from edges)
   * @default 40
   * @min 0 (no transparent center)
   * @max 80 (large transparent center)
   * Represents the percentage of the radius where the transparent center ends
   */
  vignetteSize?: number;

  /**
   * Additional CSS classes to apply to the vignette overlay
   * @default ""
   * @example "z-20 opacity-50"
   */
  vignetteClassName?: string;

  /**
   * Enable inner glow overlay (glowing effect from center)
   * @default false
   * When true, adds a glowing effect that emanates from the center and fades outward
   */
  enableInnerGlow?: boolean;

  /**
   * Color of the inner glow overlay
   * @default "rgb(0,0,0)" (black)
   * @example "rgb(59, 130, 246)" for blue glow
   * @example "rgba(255,255,255,0.8)" for semi-transparent white glow
   * Accepts any valid CSS color value
   */
  innerGlowColor?: string;

  /**
   * Opacity of the inner glow overlay
   * @default 0.7
   * @min 0 (transparent)
   * @max 1 (fully opaque)
   */
  innerGlowOpacity?: number;

  /**
   * Size of the inner glow (how far it extends from center)
   * @default 25
   * @min 0 (very small glow)
   * @max 50 (large glow covering half the area)
   * Represents the percentage of the radius where the glow extends
   */
  innerGlowSize?: number;

  /**
   * Size of the solid core of the inner glow
   * @default 0
   * @min 0 (no solid core, immediate fade)
   * @max innerGlowSize (solid up to the edge)
   * Represents the percentage where the solid color ends and fading begins
   * Must be less than or equal to innerGlowSize
   */
  innerGlowCoreSize?: number;

  /**
   * Additional CSS classes to apply to the inner glow overlay
   * @default ""
   * @example "z-30 opacity-50"
   */
  innerGlowClassName?: string;
}
export default function DottedBackground({
  className = "",
  backgroundColor = "#000",
  variant = "default",
  primaryColor,
  secondaryColor,
  dotColor,
  dotOpacity = 1,
  dotHoverOpacity,
  dotClassName = "",
  dotSpacing,
  animationSpeed = "normal",
  intensity = "high",
  dotSize = 2,
  rotation = 315,
  // -45deg equivalent
  customBlur,
  customBrightness,
  customAnimationDuration,
  customHueDuration,
  disableHueRotation = false,
  disableGradientMovement = false,
  gradientOpacity = 1,
  gradientHoverOpacity,
  gradientZIndex = 0,
  dotZIndex = 1,
  zIndex,
  enableCircleOverlay = false,
  circleOverlayColor = "#000000",
  circleOverlayOpacity = 0.8,
  circleOverlaySize = 30,
  circleOverlayZIndex = 2,
  overlayClassName = "",
  enableVignette = false,
  vignetteColor = "rgb(0,0,0)",
  vignetteOpacity = 0.9,
  vignetteSize = 40,
  vignetteZIndex = 2,
  vignetteClassName = "",
  enableInnerGlow = false,
  innerGlowColor = "rgb(0,0,0)",
  innerGlowOpacity = 0.7,
  innerGlowSize = 25,
  innerGlowCoreSize = 0,
  innerGlowZIndex = 2,
  innerGlowClassName = ""
}: DottedBackgroundProps) {
  // Build class names based on props
  const variantClass = variant !== "default" ? styles[`variant-${variant}`] : "";
  const speedClass = styles[`speed-${animationSpeed}`];
  const intensityClass = styles[`intensity-${intensity}`];
  const rotationClass = styles[`rotation-${rotation}`];

  // Build CSS variables for custom overrides
  const cssVariables = {
    "--background-color": backgroundColor,
    ...(primaryColor && {
      "--primary-gradient-color": primaryColor
    }),
    ...(secondaryColor && {
      "--secondary-gradient-color": secondaryColor
    }),
    ...(dotColor && {
      "--dot-color": dotColor
    }),
    "--dot-opacity": dotOpacity,
    ...(dotHoverOpacity !== undefined && {
      "--dot-hover-opacity": dotHoverOpacity
    }),
    "--gradient-opacity": gradientOpacity,
    ...(gradientHoverOpacity !== undefined && {
      "--gradient-hover-opacity": gradientHoverOpacity
    }),
    "--gradient-z-index": gradientZIndex,
    "--dot-z-index": dotZIndex,
    ...(dotSize && {
      "--dot-size": `${dotSize}px`
    }),
    ...(dotSpacing && {
      "--dot-spacing": `${dotSpacing}px`
    }),
    ...(customBlur && {
      "--blur-intensity": customBlur
    }),
    ...(customBrightness && {
      "--brightness-intensity": customBrightness
    }),
    ...(customAnimationDuration && {
      "--animation-duration": customAnimationDuration
    }),
    ...(customHueDuration && {
      "--hue-animation-duration": customHueDuration
    }),
    ...(zIndex !== undefined && {
      zIndex
    })
  } as React.CSSProperties;

  // Handle animation disabling
  const animationStyle: Record<string, string> = {};
  if (disableHueRotation) {
    animationStyle["--hue-animation-duration"] = "0s";
  }
  if (disableGradientMovement) {
    animationStyle["--animation-duration"] = "0s";
  }
  return <div className={`${styles.container} ${variantClass} ${speedClass} ${intensityClass} ${rotationClass} ${dotClassName} ${className}`} style={{
    ...cssVariables,
    ...animationStyle
  }} data-sentry-component="DottedBackground" data-sentry-source-file="dotted-background.tsx">
      {/* Circular overlay */}
      {enableCircleOverlay && <div className={`pointer-events-none absolute inset-0 ${overlayClassName}`} style={{
      background: `radial-gradient(circle at 50% 50%, ${circleOverlayColor} 0%, transparent ${circleOverlaySize}%)`,
      opacity: circleOverlayOpacity,
      zIndex: circleOverlayZIndex
    }} />}

      {/* Vignette overlay */}
      {enableVignette && <div className={`pointer-events-none absolute inset-0 ${vignetteClassName}`} style={{
      background: `radial-gradient(circle at 50% 50%, transparent ${vignetteSize}%, ${vignetteColor} 100%)`,
      opacity: vignetteOpacity,
      zIndex: vignetteZIndex
    }} />}

      {/* Inner glow overlay */}
      {enableInnerGlow && <div className={`pointer-events-none absolute inset-0 ${innerGlowClassName}`} style={{
      background: innerGlowCoreSize > 0 ? `radial-gradient(circle at 50% 50%, ${innerGlowColor} 0%, ${innerGlowColor} ${Math.min(innerGlowCoreSize, innerGlowSize)}%, transparent ${innerGlowSize}%)` : `radial-gradient(circle at 50% 50%, ${innerGlowColor} 0%, transparent ${innerGlowSize}%)`,
      opacity: innerGlowOpacity,
      zIndex: innerGlowZIndex
    }} />}
    </div>;
}